import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, Pencil, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { useCreateStaff, useStaff, useUpdateStaff } from '../hooks/use-staff'
import type { StaffMember, StaffUpdateRequest } from '@/api/types'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import { PermissionGate } from '@/shared/components/permission-gate'
import { ACTIONS } from '@/shared/constants/actions'
import { usePermissions } from '@/shared/hooks/use-permissions'

type StaffFormState = {
  displayName: string
  role: string
  status: 'active' | 'disabled'
  pin: string
  restaurantIds: Array<number>
}

const EMPTY_FORM: StaffFormState = {
  displayName: '',
  role: 'STAFF',
  status: 'active',
  pin: '',
  restaurantIds: [],
}

export function StaffPage() {
  const { t: tStaff } = useTranslation('staff')
  const { t: tCommon } = useTranslation('common')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<StaffMember | null>(null)
  const [form, setForm] = useState<StaffFormState>(EMPTY_FORM)
  const [restaurantFilter, setRestaurantFilter] = useState<number | 'all'>(
    'all',
  )
  const { can } = usePermissions()
  const canManage = can(ACTIONS.STAFF_MANAGE)
  const isReadOnly = !canManage

  const { data: restaurantsData } = useRestaurants()
  const restaurants = restaurantsData?.items || []
  const restaurantMap = useMemo(
    () => new Map(restaurants.map((r) => [r.id, r.name])),
    [restaurants],
  )

  const {
    data: staffData,
    isLoading,
    error,
  } = useStaff(restaurantFilter === 'all' ? null : restaurantFilter)
  const staff = staffData?.items || []

  const createStaff = useCreateStaff()
  const updateStaff = useUpdateStaff()

  useEffect(() => {
    if (dialogOpen) return
    setEditing(null)
    setForm(EMPTY_FORM)
  }, [dialogOpen])

  function openCreate() {
    if (!canManage) return
    setEditing(null)
    setForm(EMPTY_FORM)
    setDialogOpen(true)
  }

  function openEdit(member: StaffMember) {
    if (!canManage) return
    setEditing(member)
    setForm({
      displayName: member.displayName,
      role: member.role,
      status: member.status,
      pin: '',
      restaurantIds: member.restaurantIds,
    })
    setDialogOpen(true)
  }

  function toggleRestaurant(id: number, checked: boolean) {
    if (!canManage) return
    setForm((prev) => {
      const next = new Set(prev.restaurantIds)
      if (checked) next.add(id)
      else next.delete(id)
      return { ...prev, restaurantIds: Array.from(next) }
    })
  }

  function validatePin(pin: string, required: boolean) {
    if (!pin && !required) return true
    return /^\d{4}$/.test(pin)
  }

  function handleSave() {
    if (!canManage) return
    if (!form.displayName.trim()) {
      toast.error(tStaff('validation.displayNameRequired'))
      return
    }
    const pinRequired = !editing
    if (!validatePin(form.pin, pinRequired)) {
      toast.error(tStaff('validation.pinInvalid'))
      return
    }
    if (editing) {
      const payload: StaffUpdateRequest = {
        displayName: form.displayName.trim(),
        role: form.role.trim() || 'STAFF',
        status: form.status,
        restaurantIds: form.restaurantIds,
      }
      if (form.pin) payload.pin = form.pin
      updateStaff.mutate(
        { id: editing.id, data: payload },
        { onSuccess: () => setDialogOpen(false) },
      )
    } else {
      createStaff.mutate(
        {
          displayName: form.displayName.trim(),
          role: form.role.trim() || 'STAFF',
          status: form.status,
          pin: form.pin,
          restaurantIds: form.restaurantIds,
        },
        { onSuccess: () => setDialogOpen(false) },
      )
    }
  }

  return (
    <PermissionGate action={ACTIONS.STAFF_VIEW}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">{tStaff('title')}</h1>
            <p className="text-muted-foreground mt-1">{tStaff('subtitle')}</p>
          </div>
          <Button onClick={openCreate} disabled={!canManage}>
            <Plus className="mr-2 h-4 w-4" />
            {tStaff('add')}
          </Button>
        </div>

        <div className="flex items-end gap-4">
          <div className="max-w-sm w-full">
            <Label htmlFor="restaurant-filter">{tStaff('filter.label')}</Label>
            <Select
              value={
                restaurantFilter === 'all' ? 'all' : restaurantFilter.toString()
              }
              onValueChange={(value) =>
                setRestaurantFilter(value === 'all' ? 'all' : Number(value))
              }
            >
              <SelectTrigger id="restaurant-filter" className="mt-1.5">
                <SelectValue placeholder={tStaff('filter.all')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{tStaff('filter.all')}</SelectItem>
                {restaurants.map((restaurant) => (
                  <SelectItem
                    key={restaurant.id}
                    value={restaurant.id.toString()}
                  >
                    {restaurant.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{tStaff('error.title')}</AlertTitle>
                <AlertDescription>
                  {tStaff('error.description')}
                </AlertDescription>
              </Alert>
            )}
            {isLoading ? (
              <div className="text-sm text-muted-foreground">
                {tStaff('loading')}
              </div>
            ) : staff.length === 0 ? (
              <div className="text-sm text-muted-foreground">
                {tStaff('empty')}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{tStaff('table.name')}</TableHead>
                    <TableHead>{tStaff('table.role')}</TableHead>
                    <TableHead>{tStaff('table.status')}</TableHead>
                    <TableHead>{tStaff('table.restaurants')}</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staff.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell className="font-medium">
                        {member.displayName}
                      </TableCell>
                      <TableCell>
                        {member.role || tStaff('dialog.rolePlaceholder')}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            member.status === 'active' ? 'default' : 'secondary'
                          }
                        >
                          {member.status === 'active'
                            ? tStaff('dialog.statusActive')
                            : tStaff('dialog.statusDisabled')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {member.restaurantIds.length ? (
                          <div className="flex flex-wrap gap-1">
                            {member.restaurantIds.map((id) => (
                              <Badge key={id} variant="outline">
                                {restaurantMap.get(id) || `#${id}`}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">
                            {tStaff('table.notAssigned')}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEdit(member)}
                          disabled={!canManage}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editing
                  ? tStaff('dialog.editTitle')
                  : tStaff('dialog.createTitle')}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="staff-name">
                  {tStaff('dialog.displayName')}
                </Label>
                <Input
                  id="staff-name"
                  value={form.displayName}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      displayName: event.target.value,
                    }))
                  }
                  placeholder={tStaff('dialog.displayNamePlaceholder')}
                  disabled={isReadOnly}
                />
              </div>
              <div className="grid gap-2 md:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="staff-role">{tStaff('dialog.role')}</Label>
                  <Input
                    id="staff-role"
                    value={form.role}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, role: event.target.value }))
                    }
                    placeholder={tStaff('dialog.rolePlaceholder')}
                    disabled={isReadOnly}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="staff-status">
                    {tStaff('dialog.status')}
                  </Label>
                  <Select
                    value={form.status}
                    onValueChange={(value) =>
                      setForm((prev) => ({
                        ...prev,
                        status: value as StaffFormState['status'],
                      }))
                    }
                    disabled={isReadOnly}
                  >
                    <SelectTrigger id="staff-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">
                        {tStaff('dialog.statusActive')}
                      </SelectItem>
                      <SelectItem value="disabled">
                        {tStaff('dialog.statusDisabled')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="staff-pin">
                  {editing
                    ? tStaff('dialog.pinLabelEdit')
                    : tStaff('dialog.pinLabelCreate')}
                </Label>
                <Input
                  id="staff-pin"
                  value={form.pin}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      pin: event.target.value.replace(/\D/g, '').slice(0, 4),
                    }))
                  }
                  placeholder={
                    editing
                      ? tStaff('dialog.pinPlaceholderEdit')
                      : tStaff('dialog.pinPlaceholderCreate')
                  }
                  inputMode="numeric"
                  maxLength={4}
                  disabled={isReadOnly}
                />
              </div>
              <div className="grid gap-2">
                <Label>{tStaff('dialog.assignedRestaurants')}</Label>
                {restaurants.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    {tStaff('dialog.noRestaurants')}
                  </div>
                ) : (
                  <div className="grid gap-2 md:grid-cols-2">
                    {restaurants.map((restaurant) => (
                      <label
                        key={restaurant.id}
                        className="flex items-center gap-2 text-sm"
                      >
                        <Checkbox
                          checked={form.restaurantIds.includes(restaurant.id)}
                          onCheckedChange={(checked) =>
                            toggleRestaurant(restaurant.id, Boolean(checked))
                          }
                          disabled={isReadOnly}
                        />
                        <span>{restaurant.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                {tCommon('cancel')}
              </Button>
              <Button
                onClick={handleSave}
                disabled={
                  isReadOnly || createStaff.isPending || updateStaff.isPending
                }
              >
                {editing
                  ? tStaff('dialog.saveEdit')
                  : tStaff('dialog.saveCreate')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </PermissionGate>
  )
}
